/**
 * User-facing product copy — peer-to-peer challenge marketplace tone.
 * TODO(migration): rename internal identifiers (totalPot, stake, wager, escrowId,
 * AcceptWinningsModal, etc.) after a coordinated schema/API pass.
 */

// Guest
export const GUEST_BROWSE_HEADLINE = 'Browsing as guest';
export const GUEST_BROWSE_MESSAGE =
  'Browse live matchups and see how LockedIn works. Create a free account when you\'re ready to fund your wallet, challenge other players, or claim challenge earnings.';

export const GUEST_REGISTRATION_TITLE = 'Create your free account to play';
export const GUEST_REGISTRATION_BODY =
  'Guests can browse LockedIn, but wallet access, challenges, transfers, and withdrawals require a free account.';

// Free tier
export const FREE_USER_FEE_COPY =
  'Free users keep 95% of the challenge pool after the 5% platform service fee.';

export const PLUS_UPSELL_COPY =
  'Upgrade to LockedIn Plus to keep more of your challenge earnings and unlock premium features.';

export const PLUS_MEMBER_FEE_COPY =
  'LockedIn Plus members keep 98% of the challenge pool after the 2% platform service fee.';

export const PLUS_ACTIVE_FEE_COPY = 'LockedIn Plus active — 2% platform service fee.';

export const PLATFORM_FEE_DISCLOSURE =
  'Free users pay a 5% platform service fee on challenge earnings. LockedIn Plus members pay 2%.';

// Wallet & subscription
export const WALLET_FUNDS_MESSAGE =
  'Wallet funds are available immediately for challenges and platform features.';

export const WALLET_WITHDRAWAL_FUNDS_COPY =
  'Withdrawals are sent to your connected bank account and typically arrive within 1–2 business days after processing.';

export const WALLET_WITHDRAWAL_TRACKING_COPY =
  'You\'ll be able to track the status of your withdrawal directly from your Wallet activity.';

export const WALLET_SUBSCRIPTION_SEPARATION =
  'Subscription payments unlock features and reduced fees. They do not add to your wallet balance.';

export const DEPOSIT_REDIRECT_TOAST =
  'Redirecting to Stripe to complete your deposit…';

export const WITHDRAW_SECTION_TITLE = 'Withdraw to Bank';

export const WITHDRAW_PAYOUT_TIMING_COPY =
  'Withdrawals are securely processed through Stripe and typically arrive within 1–2 business days. Processing times may vary based on your bank and account verification status.';

export const WITHDRAW_SUBMITTED_TITLE = 'Withdrawal Requested';

export const WITHDRAW_SUBMITTED_BODY =
  'Your withdrawal request has been received and is being processed. Most withdrawals arrive within 1–2 business days. You can monitor the status from your Wallet activity.';

export const WITHDRAW_DUPLICATE_TOAST =
  'Withdrawal already submitted.';

export const CONNECT_BANK_REDIRECT_TOAST =
  'Redirecting to Stripe to connect your bank account…';

export const OPEN_ARENA_SECTION_TITLE = 'Live Arena';
export const OPEN_ARENA_EMPTY_COPY =
  'No open locks right now. Create one and invite the arena.';
export const OPEN_ARENA_BROWSE_LABEL = 'Browse Arena';

export const SUBSCRIPTION_REDIRECT_TOAST =
  'Redirecting to Stripe to start LockedIn Plus…';

export const BILLING_PORTAL_REDIRECT_TOAST =
  'Opening Stripe billing portal…';

export const AVAILABLE_BALANCE_LABEL = 'Available balance';
export const LOCKED_IN_ESCROW_HINT =
  'Locked challenge funds appear under Active Challenges.';

// Challenge receipts
export const CHALLENGE_REVIEW_TITLE = 'Review your challenge before locking funds.';
export const FEE_SETTLEMENT_DISCLAIMER =
  'Final transfer amount is calculated at settlement based on the winner\'s subscription status.';

// Claim earnings (internal export names unchanged — values are user-facing)
export const CLAIM_WINNINGS_TITLE = 'Challenge earnings ready to claim';
export const CLAIM_WINNINGS_BODY =
  'Your challenge has settled. Review the transfer breakdown before funds are added to your wallet.';

export const CLAIM_EARNINGS_BUTTON = 'Claim earnings';

// Inbox / action labels
export const INBOX_ACTION_REQUIRED = 'Action required';
export const INBOX_CLAIM_WINNINGS = 'Claim earnings';
export const INBOX_CHALLENGE_RECEIVED = 'Challenge received';
export const INBOX_COUNTER_RECEIVED = 'Counter offer received';

// Canonical notification titles — single source for the inbox record, the FCM
// push, and the homepage CompactAlertBanner so the same event never shows
// divergent copy across transports. The server mirrors these exact strings in
// functions/notification_handlers.py (inbox/push) and
// functions/challenge_notification.py (challenge title). Keep them in sync.
export const NOTIF_CHALLENGE_TITLE = 'New challenge';
export const NOTIF_CHALLENGE_GROUP_TITLE = 'Challenges';
export const NOTIF_COUNTER_OFFER_TITLE = 'Counter offer';
export const NOTIF_FRIEND_REQUEST_TITLE = 'Friend requests';

// ── Navigation & section labels (Sprint 4 WI-3 — canonical IA vocabulary) ──
// One label per concept so navigation, sections, and actions never diverge:
//   Board            = main game browsing area
//   Live Games       = live strip · Upcoming Games = upcoming feed
//   My Arena         = the user's own challenges/activity
//   Arena Social     = social/community area (distinct from My Arena)
//   Friends          = friends + direct messages entry (the bottom-nav social tab)
//   Chat             = actual messaging only (never used to mean "social")
//   LockedIn Plus    = the subscription (see PLUS_* copy above)
// Keep these as the single source for these visible strings.
export const NAV_BOARD_LABEL = 'Board';
export const NAV_MY_ARENA_LABEL = 'My Arena';
export const NAV_GROUPS_LABEL = 'Groups';
export const NAV_WALLET_LABEL = 'Wallet';
export const NAV_FRIENDS_LABEL = 'Friends';
export const NAV_PROFILE_LABEL = 'Profile';

export const SECTION_LIVE_GAMES = 'Live Games';
export const SECTION_UPCOMING_GAMES = 'Upcoming Games';
export const SECTION_MY_ARENA = 'My Arena';
export const SECTION_ARENA_SOCIAL = 'Arena Social';

// The "all sports" pill on the sport filter (was "Explore" — a stray term).
export const SPORT_FILTER_ALL_LABEL = 'All';

// The single creation entry point. Used by the Start-a-Lock card, the hub
// chooser, and the per-game chooser so the action is named the same everywhere.
export const BROWSE_GAMES_LABEL = 'Browse games';
export const START_A_LOCK = 'Start a Lock';

// My Arena sub-tabs. "Record" replaces "History": the tab shows an aggregate
// win/loss record, not a per-challenge history list.
export const MY_ARENA_TAB_ACTIVE = 'Active';
export const MY_ARENA_TAB_OPEN = 'Open';
export const MY_ARENA_TAB_RECORD = 'Record';
// Read-only terminal history (settled/declined/expired) so a finished challenge
// never just disappears — "what happened to my challenge?" (Sprint 9 WI-3).
export const MY_ARENA_TAB_COMPLETED = 'Completed';
// Read-only aggregation of everything the viewer initiated and is still tracking
// (outgoing challenges, open tickets, open locks) — "what did I send?"
// (Sprint 10 WI-3). Aggregation layer only; source objects stay authoritative.
export const MY_ARENA_TAB_SENT = 'Sent';
// Normalized sender-cancel labels (Sprint 10 WI-4, Decision C). UI only — backend paths unchanged.
export const SENT_CANCEL_CHALLENGE = 'Cancel challenge';
export const SENT_CANCEL_TICKET = 'Cancel ticket';
export const SENT_CANCEL_LOCK = 'Cancel lock';
// CTA appended to lifecycle transition toasts (WI-3 Step 2).
export const COMPLETED_VIEW_IN_ARENA = 'View in Completed.';
// CTA for sender-management transitions routed to the Sent surface (Sprint 10 WI-6).
export const SENT_VIEW_IN_ARENA = 'View in Sent.';
